import React, { FC, useEffect, useRef, useState } from 'react';
import { EditImproveProjectModalProps } from './EditImproveProjectModalProps';
import useTranslation from '../../hooks/useTranslation';
import CustomModal from '../../components/CustomModal';
import { ESTABLISHMENT_TYPE } from '../../components/ProjectTypeSelect/utils';
import ProjectForm from '../../components/ProjectForm';
import ImproveTabs from '../../components/ImproveTabs';
import { useTypedSelector } from '../../store';
import { selectConfidentialPermission } from '../../store/userData/selectors';
import { selectResponsibleOptionsList } from '../../store/users/selectors';
import { getBusinessUnits } from '../../store/selectData/selectors';
import { returnImproveUnitValidationSchema } from '../ImproveProjectModal/utils';
import {
  selectSiteLocation,
  selectSolutions,
} from '../../store/projects/selectors';
import {
  fetchSiteLocation,
  updateProject,
} from '../../store/projects/actionWrapper';
import { useDispatch } from 'react-redux';
import { calculateTabErrors } from '../CreateNewUnitModal/utils';
import { modalsActions } from '../../store/modals/slice';
import { ProjectFormRef } from '../../components/ProjectForm/ProjectForm';
import { stripOfNewId } from '../BulkCreate/utils';

const EditImproveProjectModal: FC<EditImproveProjectModalProps> = ({
  projectsList,
  project,
  closeHandler,
  establishmentType,
  countries,
  setActiveProject,
  disableRestrictions,
  additionalAction,
  readonly,
  submitTrigger,
  directiveOpen: initialDirectiveOpen,
}) => {
  const solutions = useTypedSelector(selectSolutions);
  const formRef = useRef<ProjectFormRef>(null);
  const dispatch = useDispatch();

  const t = useTranslation();
  const isConfidentialAllowed = useTypedSelector(selectConfidentialPermission);
  const usersList = useTypedSelector(selectResponsibleOptionsList);
  const BuList = useTypedSelector(getBusinessUnits);

  const selectedSiteLocation = useTypedSelector(selectSiteLocation);
  const [formErrors, setFormErrors] = useState({});
  const validationSchema = returnImproveUnitValidationSchema(t, project?.isep);
  const [formModified, setFormModified] = React.useState(false);
  const [selectedCountry, setSelectedCountry] = React.useState(
    project?.countryCode || '',
  );
  const [statusUpdateConfirmationModal, setStatusUpdateConfiramtionModal] =
    useState(false);
  const [directiveOpen, setDirectiveOpen] = useState(initialDirectiveOpen);
  useEffect(() => {
    setDirectiveOpen(initialDirectiveOpen);
  }, [initialDirectiveOpen, establishmentType]);
  const editCloseProjectHandler = (data) => {
    submitTrigger();
    dispatch(
      updateProject({
        project: {
          ...data,
          solutions: solutions.map(stripOfNewId),
          establishmentType: ESTABLISHMENT_TYPE.IMPROVE,
        },
        action: additionalAction,
      }),
    );
    setFormModified(false);
    closeHandler();
  };

  const onCountryChange = (country) => {
    setSelectedCountry(country.target.value);
  };

  const onBUChange = (bu) => {
    dispatch(fetchSiteLocation(bu.businessUnitId || ''));
  };

  const BuFiltered = BuList.filter(
    (bu) => bu.countryId === (selectedCountry || project?.countryCode),
  );
  const openStatusConfirmationModal = () => {
    setStatusUpdateConfiramtionModal(true);
  };
  const closeStatusConfirmationModal = () => {
    setStatusUpdateConfiramtionModal(false);
  };

  const updateStatusHandler = (id) => {
    setActiveProject(id);
    dispatch(modalsActions.openProjectStatusModal());
  };

  const updateStatusClicked = (id: string) => {
    if (formModified) {
      openStatusConfirmationModal();
      return;
    }
    updateStatusHandler(id);
  };

  const proceedWithStatusChange = () => {
    formRef.current.submit();
    closeStatusConfirmationModal();
    updateStatusHandler(project.id);
  };

  if (!project) return <></>;

  return (
    <CustomModal
      isVisible={establishmentType === ESTABLISHMENT_TYPE.IMPROVE}
      onClose={closeHandler}
      formModified={formModified}
      title={
        directiveOpen ? t('labels.projectDirective') : t('common.editProject')
      }
      onBackBtnClick={directiveOpen ? () => setDirectiveOpen(false) : undefined}
      useConfirmation
      statusConfirmationModal={statusUpdateConfirmationModal}
      closeStatusConfirmationModal={closeStatusConfirmationModal}
      proceedStatusUpdate={proceedWithStatusChange}
      children={
        <ProjectForm
          ref={formRef}
          directiveOpen={directiveOpen}
          onDirectiveBack={() => setDirectiveOpen(false)}
          onSubmit={editCloseProjectHandler}
          setFormModified={(isDirty) => setFormModified(isDirty)}
          project={project}
          onClose={closeHandler}
          isEdit
          validationSchema={validationSchema}
          selectedSiteLocation={selectedSiteLocation}
          submitBtnTitle={t('common.update')}
          projectType={ESTABLISHMENT_TYPE.IMPROVE}
          setFormErrors={setFormErrors}
          disableRestrictions={disableRestrictions}
          readOnly={readonly}
        >
          <ImproveTabs
            projectsList={projectsList}
            currentProject={project}
            countries={countries}
            setActiveProject={updateStatusClicked}
            usersList={usersList}
            onCountryChange={onCountryChange}
            onBUChange={onBUChange}
            isConfidentialAllowed={isConfidentialAllowed}
            BuList={BuFiltered}
            tabErrors={calculateTabErrors(formErrors, true)}
            isEdit
            directiveOpen={directiveOpen}
            onShowDirective={() => setDirectiveOpen(true)}
            onHideDirective={() => setDirectiveOpen(false)}
          />
        </ProjectForm>
      }
    />
  );
};

export default EditImproveProjectModal;
